import api from './client';
import {
  Ticket,
  TicketAttachment,
  TicketWithMessages,
  CreateTicketRequest,
  AddTicketMessageRequest,
  AnalyzeTicketRequest,
  TicketStatus,
  TicketPriority,
  TicketEvent,
  PageResponse,
} from '../types';

export type TicketStatusFilter = 'open' | 'closed' | 'all';
export type TicketPriorityFilter = 'all' | TicketPriority;

export interface AnalysisSource {
  kind: string;
  label: string;
}

export interface TicketAnalysisDraft {
  text: string;
  sources: AnalysisSource[];
}

export const ticketApi = {
  listByMachine: (
    machineId: string,
    params?: { page?: number; size?: number; filter?: TicketStatusFilter; priority?: TicketPriorityFilter },
  ) =>
    api.get<PageResponse<Ticket>>(`/api/machines/${machineId}/tickets`, {
      params: {
        page: params?.page ?? 0,
        size: params?.size ?? 10,
        filter: params?.filter ?? 'open',
        priority: params?.priority ?? 'all',
      },
    }),
  create: (machineId: string, data: CreateTicketRequest) =>
    api.post<TicketWithMessages>(`/api/machines/${machineId}/tickets`, data),
  get: (ticketId: string) =>
    api.get<TicketWithMessages>(`/api/tickets/${ticketId}`),
  addMessage: (ticketId: string, data: AddTicketMessageRequest) =>
    api.post<TicketWithMessages>(`/api/tickets/${ticketId}/messages`, data),
  setStatus: (ticketId: string, status: TicketStatus) =>
    api.patch<Ticket>(`/api/tickets/${ticketId}/status`, null, { params: { status } }),
  reopen: (ticketId: string) =>
    api.post<Ticket>(`/api/tickets/${ticketId}/reopen`),
  uploadAttachment: (ticketId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<TicketAttachment>(`/api/tickets/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (ticketId: string, attachmentId: string) =>
    api.delete(`/api/tickets/${ticketId}/attachments/${attachmentId}`),
  analyze: (ticketId: string, data: AnalyzeTicketRequest) =>
    api.post<TicketAnalysisDraft>(`/api/tickets/${ticketId}/analyze`, data),
  verifyMessage: (ticketId: string, messageId: string) =>
    api.post<TicketWithMessages>(`/api/tickets/${ticketId}/messages/${messageId}/verify`),
  setMessageFeedback: (ticketId: string, messageId: string, helpful: boolean) =>
    api.post<TicketWithMessages>(
      `/api/tickets/${ticketId}/messages/${messageId}/feedback`,
      null,
      { params: { helpful } }
    ),
  setPriority: (ticketId: string, priority: TicketPriority) =>
    api.patch<Ticket>(`/api/tickets/${ticketId}/priority`, null, { params: { priority } }),
  assign: (ticketId: string, userId: string | null) =>
    api.patch<Ticket>(`/api/tickets/${ticketId}/assign`, null, { params: userId ? { userId } : {} }),
  listAll: () => api.get<Ticket[]>('/api/admin/tickets'),
  listMine: (includeClosed = false) =>
    api.get<Ticket[]>('/api/my-tickets', { params: { includeClosed } }),
  timeline: (ticketId: string) =>
    api.get<TicketEvent[]>(`/api/tickets/${ticketId}/timeline`),
};
